// Shared primitives for the jam sync layer: a clip's data, and the poller's diff
// format.
//
// Lives in `shared/` because THREE consumers need them and none may own them:
// the extension, the JamRoom Durable Object, and jam-sim. The Worker cannot
// import from the extension's `src/` (no Node types, no SDK, and `nodejs_compat`
// is off), so anything the DO must understand has to live here and stay free of
// `node:` imports.

// A single field change, identical to the inspector's diff format. `path` is a
// dotted/bracketed path into the snapshot, e.g. "audioTracks[3].mute".
export interface DiffEntry {
  path: string;
  before: unknown;
  after: unknown;
}

import type { WireNote } from "../midi/notes.js";

/** What a clip's content is. Absent means "audio" — every clip that existed
 * before MIDI sync is an audio clip, and old peers never write the field. */
export type ClipKind = "audio" | "midi";

// Everything needed to recreate one arrangement clip on a peer: createAudioClip
// / createMidiClip args plus the scalar props applied after creation.
//
// MIDI clips reuse this ONE shape rather than a sibling type so every spread,
// Object.assign, SQL row and viz consumer keeps working: the 13 audio scalars
// are filled with constants (filePath "", isWarped false, warpModeValue 0), and
// the MIDI content rides in the optional fields below. Geometry for a MIDI clip
// is already FLATTENED (looping false, markers 0..duration) — see
// shared/midi/notes.ts flattenMidiClip.
/**
 * The only part of a clip's file path the wire carries (2026-10-22, finding
 * 12): its last segment. Every clip record used to ride with the proposer's
 * absolute path — `C:/Users/<name>/…`, `/Users/<name>/Desktop/…` — and was
 * broadcast to every participant and kept by the room forever. Receivers
 * resolve audio by `audioKey`, never by this path; the name is kept for the
 * scratch file and for a human reading the room. Either separator counts: a
 * Windows path reaches a POSIX room as it was typed. `""` stays `""` (a clip
 * made in a browser).
 */
export function wireFileName(filePath: string): string {
  if (!filePath) return "";
  const segments = filePath.split(/[\\/]+/).filter((s) => s.length > 0);
  return segments.length > 0 ? segments[segments.length - 1]! : "";
}

export interface ClipData {
  /** The clip's file NAME (see `wireFileName`) — older rooms hold full paths. */
  filePath: string;
  startTime: number;
  duration: number;
  isWarped: boolean;
  looping: boolean;
  startMarker: number;
  endMarker: number;
  loopStart: number;
  loopEnd: number;
  name: string;
  color: number;
  muted: boolean;
  warpModeValue: number;
  /** Absent ⇒ "audio". */
  clipKind?: ClipKind;
  /** MIDI only. Present on creates and on content changes; an update that
   * omits it keeps the room's notes (same idiom as a null audioKey). */
  notes?: WireNote[];
  /** MIDI only. Digest of `notes` — the "content changed" signal that
   * geometry can't carry (see shared/midi/notes.ts). */
  notesDigest?: string;
  /**
   * SESSION VIEW clip: the 0-based clip-slot (scene) index the clip sits in
   * on its track. Absent ⇒ an ARRANGEMENT clip placed at `startTime` — every
   * clip that predates session sync, and everything old peers write.
   *
   * A session clip has no timeline position: `startTime` is 0 by convention
   * and never compared; its identity within a lane is the slot, exactly as an
   * arrangement clip's is its startTime (shared/sync/canonical.ts
   * `clipPlaceKey`). `duration` is the clip's own length — the loop length
   * for a looping clip, else the marker span — which is what a receiver
   * needs to build the same thing (`ClipSlot.createMidiClip(length)`, and the
   * flattened MIDI notes are computed for exactly that length).
   */
  slot?: number;
  /**
   * MIDI SESSION VIEW clips only: Live's clip Loop switch. `looping` above is
   * the flattened geometry (always false for MIDI), so it can't say whether a
   * launch loops; this does. Live: "An unlooped clip will play from its
   * start point to its end point or until it is stopped." Absent ⇒ unknown
   * (every clip that predates it): a browser loops it, a receiver leaves its
   * own clip's switch alone. An update without it keeps the room's.
   */
  slotLooping?: boolean;
  /**
   * WARPED audio only (2026-09-08): the tempo the object's beat markers are
   * measured at — the tempo it was rendered or recorded at, or the tempo
   * detected in an imported file — so `startMarker` beat 4 is file second
   * 4 × 60 / sourceTempo. Live keeps this in its warp analysis; a browser
   * needs it to play a warped clip at the ROOM's tempo (stretched, or
   * re-pitched in Live's Re-Pitch mode) and to keep it on the grid when
   * the tempo changes. The merge engine stamps the room's tempo on a warped
   * audio create (or on new audio for an existing clip) that carries none,
   * since a Set renders at the room's tempo; an update without it keeps the
   * room's. Absent ⇒ unknown: the object plays at its own speed, as before.
   * Never on a digest surface; a receiver ignores it.
   */
  sourceTempo?: number;
  /**
   * WARPED audio only (slice 49): Live's warp markers for the object — the
   * exact map from the clip's beats to file seconds that `sourceTempo`
   * approximates with one number. Sorted by beat, at most
   * MAX_WARP_MARKERS of them. A Set ships them with a Session View clip's
   * source file (a timeline clip's object is a flattened render and has
   * none); a browser plays the object onto the room's grid through them
   * when there are two or more, falling back to `sourceTempo` past the
   * last one. Never on a digest surface; a receiver ignores them (the SDK
   * cannot set markers). An update shipping NEW audio without them drops
   * the old ones (a new object, a new map).
   */
  warpMarkers?: WarpMarkerData[];
  /**
   * AUDIO only (browser clip editor): Live's clip Transpose knob, whole
   * semitones in ±MAX_TRANSPOSE. Non-destructive — the object is untouched
   * and the browser shifts at playback: an unwarped clip speeds up with it
   * (vinyl, as in Live), a warped clip keeps its length, and Re-Pitch mode
   * ignores it (Live greys the knob out). Absent ⇒ 0; an update without it
   * keeps the room's. The SDK has no pitch setter, so Live never hears it.
   */
  transpose?: number;
  /** AUDIO only: Live's Detune knob, cents in ±MAX_DETUNE, on top of
   * `transpose` and under the same rules. */
  detune?: number;
  /**
   * AUDIO only (browser clip editor): the knobs of Live's warp modes that
   * the SDK does not expose — Beats' Preserve / Transient Loop Mode /
   * Transient Envelope, Complex Pro's Formants / Envelope. Every field is
   * optional and Live's default when absent; the object outlives a mode
   * switch, as Live's settings do. Validated field by field
   * (`warpSettingsOf`). Absent ⇒ defaults; an update without it keeps the
   * room's. Live never hears it.
   */
  warpSettings?: WarpSettingsData;
  /**
   * AUDIO only: this clip came from a producer's clip library ("Mine") —
   * which clip, and whose. The placement's provenance, carried through every
   * copy, trim, split and bounce and into saved versions, so attribution can
   * be built on it. Never part of a digest, never Live content: receivers
   * ignore it, and an update without it keeps the room's.
   */
  libraryClip?: LibraryClipRef;
  /**
   * AUDIO only: a flattened render of the clip AS THE BROWSER HEARS IT, kept
   * beside the original (the original, its markers, knobs and loop stay the
   * canonical clip, so the browser editor stays non-destructive). Live's SDK
   * cannot set warp markers, pitch or the mode knobs, so a receiver that
   * cannot reproduce the browser's warp builds the clip from this render
   * instead (shared/sync/clip-render.ts). Present ⇒ the clip has warp only a
   * render can carry; `key` absent, or `hash` not the clip's current
   * `warpRenderHash`, ⇒ the render is pending or stale and the original
   * plays. An update without it keeps the room's; new audio drops it.
   */
  render?: ClipRender;
}

/** See `ClipData.render`. */
export interface ClipRender {
  /** The render's R2 object key; absent while it is being made. */
  key?: string;
  /** `warpRenderHash` of the clip this render was made from. */
  hash: string;
  /** The room tempo it was rendered at: it is a warped object at this tempo. */
  tempo: number;
}

/** Longest hash or key a ClipRender may carry. */
export const MAX_RENDER_FIELD = 128;

export interface LibraryClipRef {
  /** The library clip's id (`c_…`). */
  id: string;
  /** Its owner's user id. */
  ownerId: string;
}

/** Longest id either half of a LibraryClipRef may carry. */
export const MAX_LIBRARY_REF_ID = 64;

/** The warp-mode knobs a browser can set (see `ClipData.warpSettings`). */
export interface WarpSettingsData {
  /** Beats — Preserve: 0 cuts at the transients, else at a grid division
   * in beats (`WARP_PRESERVE_DIVISIONS`: 4 is a bar of 4/4, 0.125 a 1/32). */
  preserve?: number;
  /** Beats — Transient Loop Mode: 0 off, 1 loop forward, 2 back-and-forth. */
  loopMode?: 0 | 1 | 2;
  /** Beats — Transient Envelope, 0–100 (100 is no fade). */
  envelope?: number;
  /** Complex Pro — Formants, 0–100 % (100 keeps them under a transpose). */
  formants?: number;
  /** Complex Pro — Envelope, 8–256 (how finely the envelope is followed). */
  proEnvelope?: number;
}

/** Live's Preserve choices: Transients, 1 Bar, 1/2, 1/4, 1/8, 1/16, 1/32. */
export const WARP_PRESERVE_DIVISIONS: ReadonlyArray<number> = [0, 4, 2, 1, 0.5, 0.25, 0.125];
export const MIN_PRO_ENVELOPE = 8;
export const MAX_PRO_ENVELOPE = 256;

/** Live's defaults — what an absent field means. */
export const WARP_SETTINGS_DEFAULTS: Required<WarpSettingsData> = { preserve: 0, loopMode: 2, envelope: 100, formants: 100, proEnvelope: 128 };

/**
 * `v` as warp settings: a copy holding only the known fields, each in
 * range (whole numbers for the sliders), or null when `v` is not an object
 * or any present field is out of range. An empty object is valid: all
 * defaults.
 */
export function warpSettingsOf(v: unknown): WarpSettingsData | null {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return null;
  const s = v as Record<string, unknown>;
  const out: WarpSettingsData = {};
  const int = (x: unknown, lo: number, hi: number): number | null =>
    typeof x === "number" && Number.isInteger(x) && x >= lo && x <= hi ? x : null;
  if (s.preserve !== undefined) {
    if (typeof s.preserve !== "number" || !WARP_PRESERVE_DIVISIONS.includes(s.preserve)) return null;
    out.preserve = s.preserve;
  }
  if (s.loopMode !== undefined) {
    const m = int(s.loopMode, 0, 2);
    if (m === null) return null;
    out.loopMode = m as 0 | 1 | 2;
  }
  if (s.envelope !== undefined) {
    const e = int(s.envelope, 0, 100);
    if (e === null) return null;
    out.envelope = e;
  }
  if (s.formants !== undefined) {
    const f = int(s.formants, 0, 100);
    if (f === null) return null;
    out.formants = f;
  }
  if (s.proEnvelope !== undefined) {
    const e = int(s.proEnvelope, MIN_PRO_ENVELOPE, MAX_PRO_ENVELOPE);
    if (e === null) return null;
    out.proEnvelope = e;
  }
  return out;
}

/** The settings as one short string, defaults left out — for keys. */
export function warpSettingsKey(s: WarpSettingsData | undefined): string {
  if (!s) return "";
  return (["preserve", "loopMode", "envelope", "formants", "proEnvelope"] as const)
    .filter((k) => s[k] !== undefined && s[k] !== WARP_SETTINGS_DEFAULTS[k])
    .map((k) => `${k[0]}${s[k]}`)
    .join("");
}

/** Live's Transpose knob range, in semitones. */
export const MAX_TRANSPOSE = 48;
/** Live's Detune knob range, in cents. */
export const MAX_DETUNE = 50;

/** One of Live's warp markers: the clip's beat `beat` is file second `sec`. */
export interface WarpMarkerData {
  beat: number;
  sec: number;
}

/** More markers than this and a clip ships none (a hand-warped take can
 * hold thousands; the wire keeps a clip row small). */
export const MAX_WARP_MARKERS = 256;

/**
 * NOTE (2026-08-17): the v2 assertion types that used to live here — AssertedClip,
 * TrackAssert, JamBatch — are gone. v2 made each peer authoritative for its own
 * view and had a "leader" broadcast a whole-Set assertion every 30s; five
 * data-loss incidents came out of that (docs/project/tasks.md). The room is now
 * the source of truth and the wire carries intents and accepted ops instead —
 * see ./intents.ts and ./state.ts.
 *
 * What survives here is what both protocols always needed: a clip's data, and the
 * poller's diff format.
 */
